/**
 * threadListCopy — the rail / sheet / category chrome, in the viewer's
 * language (2026-09-09). House voice: lowercase, terse; "Prest0n" keeps its
 * capital where it appears. Chrome only — never content (D9).
 */
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import type { ThreadListLang } from '../../types/threadCategories';

export interface ThreadListCopy {
  routines: string;
  noCategory: string;
  newCategory: string;
  newCategoryPlaceholder: string;
  moveTo: (title: string) => string;
  filedUnder: (section: string) => string;
  moveThread: (title: string) => string;
  threadActions: (title: string) => string;
  sectionActions: (section: string) => string;
  rename: string;
  renameSection: string;
  deleteSection: string;
  moveUp: string;
  moveDown: string;
  collapse: string;
  expand: string;
  manageSections: string;
  /** The sections manager: its heading, its close control, the empty-section count. */
  sectionsHeading: string;
  done: string;
  emptySection: string;
  pin: string;
  unpin: string;
  archive: string;
  movedTo: (section: string) => string;
  sectionRemoved: (section: string) => string;
  sectionRenamed: (section: string) => string;
  /** A DEFAULT section was renamed: it is the viewer's now, and the default came back. */
  sectionAdopted: (section: string, defaultName: string) => string;
  /** The viewer's own section was renamed to a default section's name: it folded in. */
  sectionMerged: (section: string, defaultName: string) => string;
  /** What a built-in section is FOR — the line whose absence started all of this. */
  sectionDefaultHome: (defaultName: string) => string;
  undo: string;
  /**
   * A name another section already wears. Kept as the field's quiet aria state
   * — the ANSWER to a clash is now the merge question below, not a refusal.
   */
  nameTaken: string;
  // ── merging two sections (2026-09-16) ──
  mergeSectionsTitle: string;
  mergeSectionsMessage: (from: string, into: string) => string;
  mergeSectionsConfirm: string;
  mergeSectionsCancel: string;
  dropHere: string;
  sectionNamePlaceholder: string;
  // ── search (PR-C, 2026-09-09: the search that suggests) ──
  searchPlaceholder: string;
  searchLabel: string;
  searchTooltip: (modKey: string) => string;
  clearSearch: string;
  jumpBackIn: string;
  recentSearches: string;
  threadsGroup: string;
  messagesGroup: string;
  filesGroup: string;
  routinesGroup: string;
  searchingEverywhere: string;
  searchFailed: string;
  retry: string;
  nothingMatches: string;
  nothingMatchesAnywhere: string;
  wasNamed: (name: string) => string;
  /** The opening of a former-name line, for a highlighted name: `was “` … `”`. */
  wasNamedPrefix: string;
  you: string;
  prest0n: string;
  nextRun: (when: string) => string;
  unread: string;
  // ── the tab strip + the mentions list (2026-09-17) ──
  /** The strip itself, and its three tabs. */
  tabsLabel: string;
  tabThreads: string;
  tabMentions: string;
  tabDirect: string;
  /** Spoken count on a tab's badge — a number alone tells a screen reader nothing. */
  tabUnreadCount: (n: number) => string;
  // ── the draggable divider (2026-09-20) ──
  /** The separator's name. It is a control, so it needs one. */
  railResizerLabel: string;
  /** What it reads out — "312" alone means nothing spoken. */
  railResizerValue: (px: number) => string;
  /** The tooltip that says the two things you cannot see: drag, and reset. */
  railResizerHint: string;
  /** The `people | all` toggle above the mentions list. */
  mentionsFromPeople: string;
  mentionsFromAll: string;
  mentionsFilterLabel: string;
  markAllRead: string;
  allCaughtUp: string;
  /** Nothing yet, and why that is normal rather than broken. */
  noMentionsYet: string;
  noDirectMessages: string;
  /** A row: who, and whether they named you or answered you. */
  mentionedYou: (who: string) => string;
  repliedToYou: (who: string) => string;
  /** The row's own expand control, and the context it reveals. */
  showContext: string;
  hideContext: string;
  contextUnavailable: string;
  /** Opening a mention lands on the message itself. */
  openMention: (who: string) => string;
}

const COPY: Record<ThreadListLang, ThreadListCopy> = {
  en: {
    routines: 'routines',
    noCategory: 'no category',
    newCategory: 'new category…',
    newCategoryPlaceholder: 'new category…',
    moveTo: (title) => `move ${title ? `“${title}”` : 'this thread'} to…`,
    filedUnder: (section) => `filed under ${section}`,
    moveThread: (title) => `move thread ${title} to a section`,
    threadActions: (title) => `actions for ${title}`,
    sectionActions: (section) => `actions for the ${section} section`,
    rename: 'rename',
    renameSection: 'rename section',
    deleteSection: 'delete section',
    moveUp: 'move up',
    moveDown: 'move down',
    collapse: 'collapse',
    expand: 'expand',
    manageSections: 'manage sections…',
    sectionsHeading: 'sections',
    done: 'done',
    emptySection: 'empty',
    pin: 'pin to top',
    unpin: 'unpin',
    archive: 'archive',
    movedTo: (section) => `moved to ${section}`,
    sectionRemoved: (section) => `“${section}” removed — its threads are back in their own sections`,
    sectionRenamed: (section) => `renamed to ${section}`,
    sectionAdopted: (section, defaultName) =>
      `“${section}” is yours now — new ones start in ${defaultName}. rename it back to merge them.`,
    sectionMerged: (section, defaultName) => `“${section}” merged into ${defaultName}`,
    sectionDefaultHome: (defaultName) => `new ${defaultName} start here`,
    undo: 'undo',
    nameTaken: 'a section already has that name',
    mergeSectionsTitle: 'merge these sections?',
    mergeSectionsMessage: (from, into) =>
      `“${into}” already has that name. everything in “${from}” moves there, and “${from}” goes away. nobody else's list changes.`,
    mergeSectionsConfirm: 'merge them',
    mergeSectionsCancel: 'keep them apart',
    dropHere: 'drop here',
    sectionNamePlaceholder: 'section name',
    searchPlaceholder: 'search threads, messages, files…',
    searchLabel: 'search threads, messages, files and routines',
    searchTooltip: (modKey) => `search · ${modKey}K`,
    clearSearch: 'clear search',
    jumpBackIn: 'jump back in',
    recentSearches: 'recent searches',
    threadsGroup: 'threads',
    messagesGroup: 'messages',
    filesGroup: 'files',
    routinesGroup: 'routines',
    searchingEverywhere: 'searching everywhere…',
    searchFailed: 'couldn’t search everything — check your connection',
    retry: 'retry',
    nothingMatches: 'nothing here matches yet',
    nothingMatchesAnywhere: 'nothing matches — anywhere',
    wasNamed: (name) => `was “${name}”`,
    wasNamedPrefix: 'was “',
    you: 'you',
    prest0n: 'Prest0n',
    nextRun: (when) => `next run ${when}`,
    unread: 'unread',
    tabsLabel: 'threads, mentions and direct messages',
    tabThreads: 'threads',
    tabMentions: 'mentions',
    tabDirect: 'direct',
    tabUnreadCount: (n) => (n === 1 ? '1 unread' : `${n} unread`),
    railResizerLabel: 'thread list width',
    railResizerValue: (px) => `${px} pixels wide`,
    railResizerHint: 'drag to resize · double-click to reset',
    mentionsFromPeople: 'people',
    mentionsFromAll: 'all',
    mentionsFilterLabel: 'show mentions from',
    markAllRead: 'mark all read',
    allCaughtUp: 'all caught up',
    noMentionsYet: 'nothing yet — when someone names you, it lands here',
    noDirectMessages: 'no one-on-one threads yet',
    mentionedYou: (who) => `${who} mentioned you`,
    repliedToYou: (who) => `${who} replied to you`,
    showContext: 'show what was around it',
    hideContext: 'hide what was around it',
    contextUnavailable: 'the rest of that conversation is in the thread',
    openMention: (who) => `open what ${who} said`,
  },
  es: {
    routines: 'rutinas',
    noCategory: 'sin categoría',
    newCategory: 'nueva categoría…',
    newCategoryPlaceholder: 'nueva categoría…',
    moveTo: (title) => `mover ${title ? `“${title}”` : 'este hilo'} a…`,
    filedUnder: (section) => `guardado en ${section}`,
    moveThread: (title) => `mover el hilo ${title} a una sección`,
    threadActions: (title) => `acciones para ${title}`,
    sectionActions: (section) => `acciones para la sección ${section}`,
    rename: 'renombrar',
    renameSection: 'renombrar sección',
    deleteSection: 'eliminar sección',
    moveUp: 'subir',
    moveDown: 'bajar',
    collapse: 'contraer',
    expand: 'expandir',
    manageSections: 'gestionar secciones…',
    sectionsHeading: 'secciones',
    done: 'listo',
    emptySection: 'vacía',
    pin: 'fijar arriba',
    unpin: 'desfijar',
    archive: 'archivar',
    movedTo: (section) => `movido a ${section}`,
    sectionRemoved: (section) => `“${section}” eliminada — sus hilos vuelven a sus propias secciones`,
    sectionRenamed: (section) => `renombrada a ${section}`,
    sectionAdopted: (section, defaultName) =>
      `“${section}” ahora es tuya — los nuevos empiezan en ${defaultName}. renómbrala de nuevo para unirlos.`,
    sectionMerged: (section, defaultName) => `“${section}” se unió a ${defaultName}`,
    sectionDefaultHome: (defaultName) => `los nuevos ${defaultName} empiezan aquí`,
    undo: 'deshacer',
    nameTaken: 'ya hay una sección con ese nombre',
    mergeSectionsTitle: '¿unir estas secciones?',
    mergeSectionsMessage: (from, into) =>
      `“${into}” ya tiene ese nombre. todo lo de “${from}” se mueve ahí y “${from}” desaparece. la lista de los demás no cambia.`,
    mergeSectionsConfirm: 'unirlas',
    mergeSectionsCancel: 'dejarlas separadas',
    dropHere: 'suelta aquí',
    sectionNamePlaceholder: 'nombre de la sección',
    searchPlaceholder: 'buscar hilos, mensajes, archivos…',
    searchLabel: 'buscar hilos, mensajes, archivos y rutinas',
    searchTooltip: (modKey) => `buscar · ${modKey}K`,
    clearSearch: 'borrar la búsqueda',
    jumpBackIn: 'retomar',
    recentSearches: 'búsquedas recientes',
    threadsGroup: 'hilos',
    messagesGroup: 'mensajes',
    filesGroup: 'archivos',
    routinesGroup: 'rutinas',
    searchingEverywhere: 'buscando en todo…',
    searchFailed: 'no se pudo buscar en todo — revisa tu conexión',
    retry: 'reintentar',
    nothingMatches: 'nada coincide todavía',
    nothingMatchesAnywhere: 'nada coincide — en ningún sitio',
    wasNamed: (name) => `antes “${name}”`,
    wasNamedPrefix: 'antes “',
    you: 'tú',
    prest0n: 'Prest0n',
    nextRun: (when) => `próxima ejecución ${when}`,
    unread: 'sin leer',
    tabsLabel: 'hilos, menciones y mensajes directos',
    tabThreads: 'hilos',
    tabMentions: 'menciones',
    tabDirect: 'directos',
    tabUnreadCount: (n) => (n === 1 ? '1 sin leer' : `${n} sin leer`),
    railResizerLabel: 'ancho de la lista de hilos',
    railResizerValue: (px) => `${px} píxeles de ancho`,
    railResizerHint: 'arrastra para ajustar · doble clic para restablecer',
    mentionsFromPeople: 'personas',
    mentionsFromAll: 'todo',
    mentionsFilterLabel: 'mostrar menciones de',
    markAllRead: 'marcar todo como leído',
    allCaughtUp: 'todo al día',
    noMentionsYet: 'nada todavía — cuando alguien te nombre, aparece aquí',
    noDirectMessages: 'aún no hay hilos de dos',
    mentionedYou: (who) => `${who} te mencionó`,
    repliedToYou: (who) => `${who} te respondió`,
    showContext: 'ver lo que había alrededor',
    hideContext: 'ocultar lo que había alrededor',
    contextUnavailable: 'el resto de esa conversación está en el hilo',
    openMention: (who) => `abrir lo que dijo ${who}`,
  },
};

export function threadListCopy(lang: ThreadListLang): ThreadListCopy {
  return COPY[lang] ?? COPY.en;
}

/** The viewer's list language (the platform's UI-language resolver). */
export function resolveThreadListLang(): ThreadListLang {
  return getUserUiLanguage() === 'es' ? 'es' : 'en';
}

export const THREAD_LIST_COPY_KEYS = Object.keys(COPY.en) as Array<keyof ThreadListCopy>;
